import { Result } from '@/domain/protocols/Result'

import { classifyHttpFailure } from '@/utils/withNetworkRetry'

/**
 * Erro que carrega o `Result` que falhou.
 *
 * Os useCases do projeto nunca lançam — devolvem `Result<T>`. O React Query, por contrato, precisa
 * que o `queryFn` **rejeite** para marcar a query como erro. Esta classe é a ponte, preservando o
 * `Result` original para quem precisar inspecionar.
 */
export class ResultError extends Error {
  constructor(readonly result: Result<unknown>) {
    super(describeResultError(result))
    this.name = 'ResultError'
  }
}

/**
 * `Result.error` e DECLARADO `string | undefined`, mas `Result.fail(error: any)` aceita e guarda
 * qualquer coisa — varios repositorios repassam um `Error` com `Result.fail(response.error)`. O tipo
 * mente, entao a leitura precisa ser defensiva em runtime.
 */
function describeResultError(result: Result<unknown>): string {
  const raw = result.error as unknown

  if (typeof raw === 'string' && raw.length > 0) return raw
  if (raw instanceof Error) return raw.message

  return 'ResultError'
}

/**
 * Executa algo que devolve `Result<T>` e lança quando falha, para o React Query enxergar o erro.
 *
 *     queryFn: () => unwrap(() => new GetUserRoutesUseCase().execute())
 *
 * Como os useCases resolvem as próprias dependências pelo container, isto funciona **fora do React**
 * — dá para escrever `queryOptions` reutilizáveis sem hook e sem closure sobre componente.
 */
export async function unwrap<T>(run: () => Promise<Result<T>> | Result<T>): Promise<T> {
  const result = await run()

  if (result.isFailure) throw new ResultError(result)

  return result.getValue()
}

/**
 * Escava o erro real por baixo do `ResultError`.
 *
 * `Result.fail` aceita `any`, e vários repositórios repassam o erro de transporte com
 * `Result.fail(response.error)` — nesses casos o `Error` original sobrevive dentro do `Result` e é
 * ele que interessa para classificar a falha.
 */
export function unwrapCause(error: unknown): unknown {
  if (error instanceof ResultError) return (error.result.error as unknown) ?? error

  return error
}

/** `true` quando a falha é flap de rede/timeout — ou seja, o transporte já gastou a cota de retry. */
export function isTransientQueryError(error: unknown): boolean {
  return classifyHttpFailure(unwrapCause(error)) === 'transient'
}

/** `true` quando o servidor respondeu com erro (5xx). Vale uma retentativa, feita pelo React Query. */
export function isServerQueryError(error: unknown): boolean {
  const cause = unwrapCause(error) as { response?: { status?: number }; httpStatus?: number } | null
  const status = cause?.response?.status ?? cause?.httpStatus

  return typeof status === 'number' && status >= 500
}
